// modals/EditSummaryModal.js
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/system';

// Full-screen backdrop to dim the background.
const ModalBackdrop = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
});

// Modal card container with ample padding and scrollability.
const ModalCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  width: '90%',
  maxWidth: 800,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: theme.shadows[5],
}));

// A custom multi-line text area component.
const CustomTextArea = styled('textarea')(({ theme }) => ({
  width: '100%',
  padding: '12px',
  fontSize: '1rem',
  fontFamily: 'inherit',
  border: '1px solid #ccc',
  borderRadius: '4px',
  resize: 'vertical',
  minHeight: '200px',
  lineHeight: 1.5,
  boxSizing: 'border-box',
}));

function EditSummaryModal({ open, onClose, currentSummary, onSave, maxLength = 5000 }) {
  const [summary, setSummary] = useState(currentSummary || '');
  const modalRef = useRef(null);
  const textareaRef = useRef(null);
  const [error, setError] = useState('');

  // Update the summary when the currentSummary prop changes or the modal opens.
  useEffect(() => {
    setSummary(currentSummary || '');
    setError('');
  }, [currentSummary, open]);

  // Focus trap and initial focus
  useEffect(() => {
    if (open && modalRef.current) {
      // Focus the textarea when modal opens
      textareaRef.current?.focus();

      // Setup focus trap
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      modalRef.current.addEventListener('keydown', handleTabKey);
      return () => {
        modalRef.current?.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [open]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      setSummary(newValue);
      setError('');
    }
  };

  const handleSave = () => {
    if (summary.trim().length === 0) {
      setError('Summary cannot be empty');
      return;
    }
    onSave(summary);
    onClose();
  };

  if (!open) return null;

  return (
    <ModalBackdrop onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="edit-summary-title">
      {/* Prevent clicks inside the modal card from closing the modal */}
      <ModalCard ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <Typography id="edit-summary-title" variant="h5" sx={{ mb: 3 }}>
          Edit Summary
        </Typography>
        <CustomTextArea
          ref={textareaRef}
          placeholder="Write your summary here..."
          value={summary}
          onChange={handleChange}
          maxLength={maxLength}
          aria-label="Faculty summary text"
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? 'summary-error' : 'summary-counter'}
        />
        {error && (
          <Typography id="summary-error" variant="caption" color="error" role="alert" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
        {/* Character counter */}
        <Typography id="summary-counter" variant="body2" sx={{ textAlign: 'right', mt: 1 }}>
          {summary.length}/{maxLength} characters
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} color="primary">
            Save
          </Button>
        </Box>
      </ModalCard>
    </ModalBackdrop>
  );
}

export default EditSummaryModal;
